import { IntakeData } from '../context/IntakeContext'
import {
  OptionMap,
  isOwnerOptions,
  isDecisionMakerOptions,
  showTeamOptions,
  teamPhotosOptions,
  multipleLocationsOptions,
  locationStrategyOptions,
  locationShowOptions,
  companyFeelOptions,
  hasLegalDocsOptions,
  legalDocOptions,
  legalApproachOptions,
  hasIdentityOptions,
  typographyOptions,
  mainGoalOptions,
  websiteTypeOptions,
  needsPortalOptions,
  pageOptions,
  textContentOptions,
  photoContentOptions,
  toneOfVoiceOptions,
  domainOwnedOptions,
  domainManagementOptions,
  offlineOptions,
  hostingOptions,
  seoImportanceOptions,
  keywordResearchOptions,
  seoSiloOptions,
  siloPackageOptions,
  blogpostsOptions,
  blogFrequencyOptions,
  keywordUpdatesOptions,
  yesNoAdviseOptions,
  yesNoOptions,
  trainingOptions,
  businessProfileTaskOptions,
  socialAdsOptions,
  launchDateOptions,
  websiteLanguagesOptions,
  yesNoUnknownOptions,
  currentCmsOptions,
  accessKnownOptions,
  integrationOptions,
  shippingCostOptions,
} from '../data/intakeOptions'
import {
  IntakeReport,
  ReportEntry,
  ReportQuestion,
  ReportSection,
  RelevantUpload,
} from '../types/intakeReport'

type UploadedFiles = Record<string, File>

/**
 * Zet alle intake-antwoorden om naar één gestructureerd rapport.
 * Alleen vragen die de klant daadwerkelijk te zien kreeg (op basis van
 * eerdere keuzes) worden opgenomen — lege antwoorden blijven zichtbaar als
 * "niet ingevuld", zodat duidelijk is wat er nog ontbreekt.
 * PDF, mail en Markdown worden hier allemaal uit gegenereerd.
 */
export function buildIntakeReport(
  data: IntakeData,
  uploadedFiles: UploadedFiles = {},
): IntakeReport {
  const sections: ReportSection[] = [
    contactSection(data),
    teamSection(data, uploadedFiles),
    companySection(data),
    storySection(data),
    legalSection(data, uploadedFiles),
    brandingSection(data, uploadedFiles),
    goalsSection(data),
    typeSection(data),
    pagesContentSection(data),
    techSection(data),
    seoSection(data),
    planningSection(data),
  ]

  return {
    clientName: data.companyName || data.contactName || 'Nieuwe klant',
    generatedAt: new Date().toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
    sections,
    files: getRelevantUploads(data, uploadedFiles).map((u) => ({
      label: u.label,
      fileName: u.file.name,
    })),
  }
}

/**
 * Alle geüploade bestanden die horen bij vragen die de klant (nog) te zien
 * krijgt. Bestanden van uitgeschakelde vragen worden niet meegestuurd.
 */
export function getRelevantUploads(
  data: IntakeData,
  uploadedFiles: UploadedFiles,
): RelevantUpload[] {
  const list: RelevantUpload[] = []
  const add = (key: string, label: string) => {
    const file = uploadedFiles[key]
    if (file) list.push({ key, label, file })
  }
  if (data.showTeamOnWebsite === 'yes' && data.teamHasPhotos === 'yes') {
    data.teamMembers.forEach((m, i) =>
      add(`team_${m.id}`, `Teamfoto — ${m.name || `Teamlid ${i + 1}`}`),
    )
  }
  if (data.hasLegalDocs === 'yes') {
    data.legalDocsList.forEach((k) =>
      add(`legal_${k}`, `Juridisch — ${legalDocOptions[k] || k}`),
    )
  }
  if (data.hasIdentity === 'yes_complete' || data.hasIdentity === 'yes_partly') {
    add('logo', 'Branding — Logo')
    if (data.hasIdentity === 'yes_complete') {
      add('guidelines', 'Branding — Huisstijlhandboek')
    }
  }
  if (data.hasIdentity === 'no') {
    add('logo_sketch', 'Branding — Logo schets / inspiratie')
  }
  return list
}

function contactSection(data: IntakeData): ReportSection {
  const entries: ReportEntry[] = [
    q('Volledige naam', text(data.contactName)),
    q('Functie / Rol', text(data.contactRole)),
    q('E-mailadres', text(data.contactEmail)),
    q('Telefoonnummer', text(data.contactPhone)),
    q('Bent u de eigenaar van het bedrijf?', opt(isOwnerOptions, data.isOwner)),
    q(
      'Bent u de uiteindelijke beslisser voor dit project?',
      opt(isDecisionMakerOptions, data.isDecisionMaker),
    ),
  ]
  if (data.isDecisionMaker === 'no') {
    data.owners.forEach((owner, i) => {
      entries.push({
        type: 'group',
        title: `Overige beslisser ${i + 1}${owner.name ? ` — ${owner.name}` : ''}`,
        items: [
          q('Naam', text(owner.name)),
          q('Functie / Rol', text(owner.role)),
          q('E-mailadres', text(owner.email)),
          q('Telefoonnummer', text(owner.phone)),
        ],
      })
    })
  }
  return {
    title: 'Contactpersoon',
    purpose: 'Wie is het aanspreekpunt en wie beslist over het project.',
    entries,
  }
}

function teamSection(data: IntakeData, files: UploadedFiles): ReportSection {
  const entries: ReportEntry[] = [
    q(
      'Wilt u teamleden op de website tonen?',
      opt(showTeamOptions, data.showTeamOnWebsite),
    ),
  ]
  if (data.showTeamOnWebsite === 'yes') {
    entries.push(
      q(
        'Wil je een foto uploaden van je teamleden?',
        opt(teamPhotosOptions, data.teamHasPhotos),
      ),
    )
    if (data.teamHasPhotos) {
      data.teamMembers.forEach((m, i) => {
        const items = [
          q('Naam', text(m.name)),
          q('Functie', text(m.role)),
          q('Verhaaltje / Bio', text(m.bio)),
        ]
        if (data.teamHasPhotos === 'yes') {
          items.push(attachment('Profielfoto', files, `team_${m.id}`))
        }
        entries.push({
          type: 'group',
          title: `Teamlid ${i + 1}${m.name ? ` — ${m.name}` : ''}`,
          items,
        })
      })
    }
  }
  return {
    title: 'Team',
    purpose: 'Inhoud voor een eventuele teampagina of "over ons" sectie.',
    entries,
  }
}

function companySection(data: IntakeData): ReportSection {
  const languages =
    data.websiteLanguages === 'more'
      ? `Meer talen: ${data.websiteLanguagesOther.trim() || 'niet gespecificeerd'}`
      : opt(websiteLanguagesOptions, data.websiteLanguages)
  const entries: ReportEntry[] = [
    q('Bedrijfsnaam (statutair)', text(data.companyName)),
    q('Handelsnaam', text(data.tradeName)),
    q('Branche / sector', text(data.industry)),
    q('KVK-nummer', text(data.cocNumber)),
    q('BTW-nummer', text(data.vatNumber)),
    q(
      'Adres hoofdvestiging',
      joinParts([
        data.mainStreet,
        [data.mainPostcode, data.mainCity].filter(Boolean).join(' '),
        data.mainProvince,
        data.mainCountry,
      ]),
    ),
    q('Algemeen telefoonnummer', text(data.companyPhone)),
    q('Algemeen e-mailadres', text(data.companyEmail)),
    q('Huidige website', text(data.existingWebsiteUrl)),
    q('Social media kanalen', text(data.socialLinks)),
    q('In welke talen moet de website beschikbaar zijn?', languages),
    q(
      'Heeft u meerdere locaties?',
      opt(multipleLocationsOptions, data.multipleLocations),
    ),
  ]
  if (data.multipleLocations === 'yes') {
    entries.push(
      q(
        'Hoe wilt u de locaties op de website verwerken?',
        opt(locationStrategyOptions, data.locationStrategy),
      ),
    )
    data.locations.forEach((loc, i) => {
      const items = [
        q('Naam locatie', text(loc.name)),
        q(
          'Adres',
          joinParts([
            loc.street,
            [loc.postcode, loc.city].filter(Boolean).join(' '),
            loc.province,
            loc.country,
          ]),
        ),
      ]
      if (data.locationStrategy === 'specific_only') {
        items.push(
          q(
            'Deze locatie tonen op de website?',
            opt(locationShowOptions, loc.showOnWebsite),
          ),
        )
      }
      entries.push({
        type: 'group',
        title: `Locatie ${i + 1}${loc.name ? ` — ${loc.name}` : ''}`,
        items,
      })
    })
  }
  return {
    title: 'Bedrijfsgegevens',
    purpose:
      'Voor contactpagina, footer, juridische teksten, taalversies en lokale SEO.',
    entries,
  }
}

function storySection(data: IntakeData): ReportSection {
  const entries: ReportEntry[] = []
  if (data.services.length === 0) {
    entries.push(q('Diensten of producten', null))
  } else {
    data.services.forEach((s, i) => {
      entries.push({
        type: 'group',
        title: `Dienst ${i + 1}${s.name ? ` — ${s.name}` : ''}`,
        items: [
          q('Naam', text(s.name)),
          q('Beschrijving', text(s.description)),
        ],
      })
    })
  }
  const feel = data.companyFeel
    .map((v) =>
      v === 'other'
        ? data.companyFeelOther.trim() || 'Anders (niet gespecificeerd)'
        : companyFeelOptions[v] || v,
    )
    .join(', ')
  entries.push(
    q('Wat maakt u anders dan concurrenten?', text(data.difference)),
    q('Wat zijn uw kernwaarden?', text(data.coreValues)),
    q('Hoe moet het bedrijf aanvoelen voor een bezoeker?', feel || null),
    q(
      "Woorden of thema's die absoluut vermeden moeten worden",
      text(data.mustAvoid),
    ),
    q('Ideale website in 2-3 zinnen', text(data.idealWebsiteDescription)),
  )
  return {
    title: 'Diensten, verhaal & positionering',
    purpose:
      "Basis voor paginastructuur, teksten, USP's en de sfeer van het design.",
    entries,
  }
}

function legalSection(data: IntakeData, files: UploadedFiles): ReportSection {
  const entries: ReportEntry[] = [
    q(
      'Zijn er juridische documenten die op de website moeten komen?',
      opt(hasLegalDocsOptions, data.hasLegalDocs),
    ),
  ]
  if (data.hasLegalDocs === 'yes') {
    entries.push(
      q(
        'Welke documenten zijn beschikbaar?',
        opts(legalDocOptions, data.legalDocsList),
      ),
    )
    data.legalDocsList.forEach((key) => {
      entries.push(
        attachment(`Upload: ${legalDocOptions[key] || key}`, files, `legal_${key}`),
      )
    })
  }
  if (data.hasLegalDocs === 'no') {
    entries.push(
      q(
        'Hoe wilt u dit aanpakken?',
        opt(legalApproachOptions, data.legalDocsApproach),
      ),
      q(
        'Welke documenten zijn nodig?',
        opts(legalDocOptions, data.legalDocsNeeded),
      ),
    )
  }
  return {
    title: 'Juridisch',
    purpose: "Welke juridische pagina's nodig zijn en wie ze aanlevert.",
    entries,
  }
}

function brandingSection(
  data: IntakeData,
  files: UploadedFiles,
): ReportSection {
  const entries: ReportEntry[] = [
    q(
      'Heeft u al een visuele identiteit?',
      opt(hasIdentityOptions, data.hasIdentity),
    ),
  ]
  const hasLogo =
    data.hasIdentity === 'yes_complete' || data.hasIdentity === 'yes_partly'
  if (hasLogo) {
    entries.push(attachment('Logo', files, 'logo'))
    if (data.hasIdentity === 'yes_complete') {
      entries.push(attachment('Huisstijlhandboek', files, 'guidelines'))
    }
  }
  if (data.hasIdentity === 'no') {
    entries.push(
      q('Ideeën voor een logo', text(data.logoIdeas)),
      attachment('Schets / inspiratie voor logo', files, 'logo_sketch'),
    )
  }
  if (data.hasIdentity) {
    entries.push({
      type: 'colors',
      question: hasLogo ? 'Huidige merkkleuren' : 'Voorkeurskleuren',
      colors: data.preferredColors,
    })
    entries.push(
      q(
        'Wensen qua typografie',
        opt(typographyOptions, data.typographyChoice),
      ),
    )
  }
  return {
    title: 'Branding & huisstijl',
    purpose: 'Kleuren, lettertype en logo voor het visuele ontwerp.',
    entries,
  }
}

function goalsSection(data: IntakeData): ReportSection {
  const goal =
    data.mainGoal === 'other'
      ? data.mainGoalOther.trim() || 'Anders (niet gespecificeerd)'
      : opt(mainGoalOptions, data.mainGoal)
  const entries: ReportEntry[] = [
    q('Wat is het primaire doel van de website?', goal),
    q(
      'Belangrijkste actie die een bezoeker moet ondernemen (CTA)',
      text(data.visitorAction),
    ),
    q('Ideale doelgroep', text(data.targetAudience)),
  ]
  if (data.existingWebsiteUrl.trim()) {
    entries.push(
      q(
        'Wat werkt er niet goed aan de huidige website?',
        text(data.betterThanCurrent),
      ),
    )
  }
  entries.push(
    q("Inspirerende websites (URL's)", text(data.inspirationWebsites)),
    q(
      'Wat moet een bezoeker voelen/denken bij het zien van de website?',
      text(data.desiredImpression),
    ),
  )
  return {
    title: 'Doelen & doelgroep',
    purpose: 'Bepaalt de call-to-actions, hiërarchie en toon van elke pagina.',
    entries,
  }
}

function typeSection(data: IntakeData): ReportSection {
  const entries: ReportEntry[] = [
    q(
      'Wat voor type website is nodig?',
      opt(websiteTypeOptions, data.websiteType),
    ),
  ]
  if (data.websiteType === 'webshop') {
    entries.push(
      q('Aantal producten (ongeveer)', text(data.numProducts)),
      q(
        'Hoe moeten de producten erin worden gezet?',
        text(data.productManagement),
      ),
      q('Gewenste betaalmethodes', text(data.paymentProviders)),
      q('Verzendmethodes', text(data.shippingMethods)),
      q(
        'Hebben producten varianten (maat, kleur)?',
        opt(yesNoOptions, data.productVariations),
      ),
      q(
        'Moet de voorraad bijgehouden worden?',
        opt(yesNoOptions, data.stockManagement),
      ),
      q(
        'Hoe wilt u verzendkosten rekenen?',
        opt(shippingCostOptions, data.shippingCosts),
      ),
    )
  }
  if (data.websiteType === 'website' || data.websiteType === 'webshop') {
    entries.push(
      q(
        'Moet er een klantenportaal komen?',
        opt(needsPortalOptions, data.needsPortal),
      ),
    )
    if (data.needsPortal === 'yes') {
      entries.push(
        q(
          'Wat moet het portaal onthouden/tonen aan de klant?',
          text(data.portalDetails),
        ),
        q('Gebruikersrollen', text(data.userRoles)),
      )
    }
  }
  return {
    title: 'Type website & functionaliteit',
    purpose:
      'Gebouwd in WordPress + Elementor (WooCommerce voor webshops). Bepaalt de technische scope.',
    entries,
  }
}

function pagesContentSection(data: IntakeData): ReportSection {
  const pages = data.neededPages
    .map((v) =>
      v === 'other'
        ? data.customPageOther.trim() || 'Anders (niet gespecificeerd)'
        : pageOptions[v] || v,
    )
    .join(', ')
  return {
    title: "Pagina's & content",
    purpose: 'Sitemap, wie de teksten/beelden levert en de schrijfstijl.',
    entries: [
      q("Gewenste pagina's", pages || null),
      q(
        'Hoe worden de teksten geregeld?',
        opt(textContentOptions, data.existingContent[0]),
      ),
      q(
        'Fotografie en beeldmateriaal',
        opt(photoContentOptions, data.createContent[0]),
      ),
      q(
        'Toon van de website (tone of voice)',
        opt(toneOfVoiceOptions, data.toneOfVoice),
      ),
    ],
  }
}

function techSection(data: IntakeData): ReportSection {
  const entries: ReportEntry[] = [
    q(
      'Heeft u al een domeinnaam?',
      opt(domainOwnedOptions, data.domainOwned),
    ),
  ]
  if (data.domainOwned === 'yes') {
    entries.push(
      q('Domeinnaam', text(data.domainName)),
      q('Waar is deze gehost?', text(data.domainHost)),
      q('Beheer domein', opt(domainManagementOptions, data.domainManagement)),
    )
    if (data.domainManagement === 'third_party') {
      entries.push(q('Welke 3e partij?', text(data.domainThirdParty)))
    }
    entries.push(
      q(
        'Draait de e-mail (bijv. info@uwbedrijf.nl) op dit domein?',
        opt(yesNoUnknownOptions, data.emailsLinked),
      ),
      q(
        'Mag de website tijdelijk offline tijdens de bouw?',
        opt(offlineOptions, data.websiteOfflineAcceptable),
      ),
    )
  }
  if (data.domainOwned === 'no') {
    entries.push(
      q(
        'Gewenste domeinnaam / domeinnamen',
        data.desiredDomains.length ? data.desiredDomains.join(', ') : null,
      ),
    )
  }
  if (data.existingWebsiteUrl.trim()) {
    entries.push(
      q(
        'In welk systeem is de huidige website gebouwd?',
        opt(currentCmsOptions, data.currentCms),
      ),
      q(
        'Heeft de klant de inloggegevens van de huidige website en het domein?',
        opt(accessKnownOptions, data.hostingAccessKnown),
      ),
      q(
        "Bestaande pagina's die moeten blijven of doorverwijzen (redirects)",
        text(data.migratePages),
      ),
    )
  }
  const integrations = data.integrations
    .map((v) =>
      v === 'other'
        ? `Anders: ${data.integrationsOther.trim() || 'niet gespecificeerd'}`
        : integrationOptions[v] || v,
    )
    .join(', ')
  entries.push(
    q('Welke koppelingen zijn nodig?', integrations || null),
    q(
      'Heeft u al Google Analytics, Search Console of advertentie-pixels (Meta, LinkedIn)?',
      opt(yesNoUnknownOptions, data.existingAnalytics),
    ),
    q('Hosting en onderhoud', opt(hostingOptions, data.hostingActive)),
  )
  return {
    title: 'Techniek & hosting',
    purpose:
      'Domein, e-mail, migratie van de huidige site, koppelingen en livegang-aanpak.',
    entries,
  }
}

function seoSection(data: IntakeData): ReportSection {
  const entries: ReportEntry[] = [
    q(
      'Hoe belangrijk is vindbaarheid in Google?',
      opt(seoImportanceOptions, data.seoServices),
    ),
  ]
  const important = data.seoServices === 'high' || data.seoServices === 'medium'
  if (important) {
    entries.push(
      q('Gewenste zoektermen', text(data.seoSearchTerms)),
      q('Belangrijkste online concurrenten', text(data.seoCompetitors)),
      q("Regio's / steden waarin gevonden worden", text(data.seoSiloRegions)),
      q(
        'Zoekwoordenonderzoek',
        opt(keywordResearchOptions, data.seoKeywordResearch),
      ),
      q('SEO silo structuur', opt(seoSiloOptions, data.seoSilo)),
    )
    if (data.seoSilo === 'yes' || data.seoSilo === 'dont_know') {
      entries.push(
        q('Silo-pakket', opt(siloPackageOptions, data.seoSiloPackage)),
        q("Hoofd-diensten / thema's voor de silo", text(data.seoSiloThemes)),
      )
    }
    entries.push(
      q(
        'SEO-geoptimaliseerde blogposts door Noord Digital',
        opt(blogpostsOptions, data.seoBlogposts),
      ),
    )
    if (data.seoBlogposts === 'yes') {
      entries.push(
        q('Blogfrequentie', opt(blogFrequencyOptions, data.seoBlogFrequency)),
      )
    }
    entries.push(
      q(
        'Zoekwoorden regelmatig analyseren en bijsturen',
        opt(keywordUpdatesOptions, data.seoActiveUpdates),
      ),
      q('Heatmaps', opt(yesNoAdviseOptions, data.seoHeatmapsUrgency)),
      q('A/B testing', opt(yesNoAdviseOptions, data.seoABTestingUrgency)),
      q(
        'Extra training (website, SEO, bedrijfsprofielen)',
        opt(trainingOptions, data.seoTraining),
      ),
      q(
        'Hulp met Google Bedrijfsprofiel',
        opt(yesNoOptions, data.seoGoogleBusiness),
      ),
    )
    if (data.seoGoogleBusiness === 'yes') {
      entries.push(
        q(
          'Taken Google Bedrijfsprofiel',
          opts(businessProfileTaskOptions, data.seoGoogleBusinessTasks),
        ),
      )
    }
    entries.push(
      q(
        'Hulp met Apple Bedrijfsprofiel',
        opt(yesNoOptions, data.seoAppleBusiness),
      ),
    )
    if (data.seoAppleBusiness === 'yes') {
      entries.push(
        q(
          'Taken Apple Bedrijfsprofiel',
          opts(businessProfileTaskOptions, data.seoAppleBusinessTasks),
        ),
      )
    }
    entries.push(
      q('Google Ads (SEA)', opt(yesNoAdviseOptions, data.seoGoogleAds)),
      q(
        'Social media advertenties',
        data.seoSocialAds.length
          ? opts(socialAdsOptions, data.seoSocialAds)
          : 'Geen',
      ),
    )
  }
  return {
    title: 'SEO & marketing',
    purpose:
      'Input voor zoekwoordenonderzoek, silo-structuur en marketingdiensten.',
    entries,
  }
}

function planningSection(data: IntakeData): ReportSection {
  const entries: ReportEntry[] = [
    q('Wanneer moet de website live?', opt(launchDateOptions, data.launchDate)),
  ]
  if (data.launchDate && data.launchDate !== 'relaxed') {
    entries.push(
      q('Is dit een harde deadline?', opt(yesNoOptions, data.hardDeadline)),
    )
    if (data.hardDeadline === 'yes') {
      entries.push(q('Reden van de deadline', text(data.deadlineReason)))
    }
  }
  entries.push(
    q('Wie geven er feedback op het ontwerp?', text(data.feedbackGivers)),
    q('Vragen of opmerkingen voor Noord Digital', text(data.remarks)),
    q('Extra notities voor het ontwikkelteam', text(data.extraNotes)),
  )
  return {
    title: 'Planning & notities',
    purpose: 'Tijdlijn, feedbackproces en alle extra wensen van de klant.',
    entries,
  }
}

function q(question: string, answer: string | null): ReportQuestion {
  return { type: 'question', question, answer }
}

function attachment(
  question: string,
  files: UploadedFiles,
  key: string,
): ReportQuestion {
  const file = files[key]
  return q(question, file ? `Bijlage in mail: ${file.name}` : null)
}

function text(value: string | undefined): string | null {
  const trimmed = (value || '').trim()
  return trimmed ? trimmed : null
}

function opt(map: OptionMap, value: string | undefined): string | null {
  if (!value) return null
  return map[value] || value
}

function opts(map: OptionMap, values: string[]): string | null {
  if (!values || values.length === 0) return null
  return values.map((v) => map[v] || v).join(', ')
}

function joinParts(parts: Array<string | undefined>): string | null {
  const joined = parts
    .map((p) => (p || '').trim())
    .filter(Boolean)
    .join(', ')
  return joined || null
}
